import { format, differenceInDays, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'yyyy.MM.dd');
  } catch {
    return dateStr;
  }
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'yyyy.MM.dd HH:mm');
  } catch {
    return dateStr;
  }
};

export const formatKoreanDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'yyyy년 MM월 dd일 (EEE)', { locale: ko });
  } catch {
    return dateStr;
  }
};

export const daysUntilExpiry = (endDate) => {
  if (!endDate) return null;
  try {
    return differenceInDays(parseISO(endDate), new Date());
  } catch {
    return null;
  }
};

export const getMembershipStatus = (membership) => {
  if (!membership) return null;
  const days = daysUntilExpiry(membership.endDate);
  if (days === null) return membership.status;
  if (membership.status === 'expired' || days < 0) return 'expired';
  if (days <= 7) return 'expiring';
  return 'active';
};

export const formatPrice = (price) => {
  if (price === null || price === undefined) return '-';
  return price.toLocaleString('ko-KR') + '원';
};

// 금액 입력 필드용: 숫자만 저장된 값을 콤마 포함 문자열로 표시
export const formatPriceInput = (val) => {
  const digits = String(val ?? '').replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('ko-KR');
};

// 콤마가 포함된 입력값에서 순수 숫자 문자열 추출
export const parsePriceInput = (val) => {
  return String(val).replace(/[^0-9]/g, '');
};

export const formatPhone = (phone) => {
  if (!phone) return '-';
  return phone;
};

// 목록 화면용 마스킹: 010-1234-5678 → 010-1234-****
export const maskPhone = (phone) => {
  if (!phone) return '-';
  return phone.replace(/(\d{3}-\d{3,4})-(\d{4})$/, '$1-****');
};

export const getAge = (birthDate) => {
  if (!birthDate) return null;
  try {
    const birth = parseISO(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  } catch {
    return null;
  }
};

export const isBirthday = (birthDate) => {
  if (!birthDate) return false;
  try {
    const birth = parseISO(birthDate);
    const today = new Date();
    return birth.getMonth() === today.getMonth() && birth.getDate() === today.getDate();
  } catch {
    return false;
  }
};

export const getTodayString = () => {
  return format(new Date(), 'yyyy-MM-dd');
};

export const getStatusLabel = (status) => {
  const map = {
    active: '이용중',
    expired: '만료',
    inactive: '휴면',
    expiring: '만료임박',
    empty: '비어있음',
    occupied: '사용중',
  };
  return map[status] || status;
};

// 운영시간 JSON → 특정 날짜의 slot 반환
// slot: { open, close, closed } | null
export const getBusinessHoursSlot = (businessHours, date = new Date()) => {
  if (!businessHours) return null;
  try {
    const h = typeof businessHours === 'string' ? JSON.parse(businessHours) : businessHours;
    const dayIdx = date.getDay(); // 0=일, 1=월, ..., 6=토
    const customDayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    if (h.custom) {
      return h[customDayKeys[dayIdx]] || null;
    }
    if (dayIdx === 0) return h.sunday || null;
    if (dayIdx === 6) return h.saturday || null;
    return h.weekday || null;
  } catch {
    return null;
  }
};

// 운영시간 slot → 표시 문자열
export const formatHoursSlot = (slot) => {
  if (!slot) return null;
  if (slot.closed) return '오늘 휴무';
  return `${slot.open} ~ ${slot.close}`;
};

// 특별 안내 문자열 반환
export const getBusinessHoursNotice = (businessHours) => {
  if (!businessHours) return null;
  try {
    const h = typeof businessHours === 'string' ? JSON.parse(businessHours) : businessHours;
    return h.notice || null;
  } catch {
    return null;
  }
};

// snake_case → camelCase (중첩 객체/배열 포함)
export function toCamel(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/_([a-z])/g, (_, c) => c.toUpperCase()),
      Array.isArray(v) ? v : toCamel(v),
    ])
  );
}

export const getStatusBadgeClass = (status) => {
  const map = {
    active: 'badge-green',
    expired: 'badge-red',
    inactive: 'badge-gray',
    expiring: 'badge-yellow',
  };
  return map[status] || 'badge-gray';
};
